import AxiosService from '@/services/api/AxiosService';
import ApiLinks from '@/entities/ApiLinks';
import ApiStatus from '@/entities/enums/apiStatuses';
import { Department } from '@/entities/models/Department';
import { ApiQueries } from '@/entities/ApiQueries';
import ApiResponse from '@/entities/ApiResponse';
import { DepartmentSettings } from '@/entities/models/DepartmentSettings';
import { AdminDepartment } from '@/entities/models/AdminDepartment';
import { AdminDepartmentSettings } from '@/entities/AdminDepartmentSettings';
import { vxm } from '@/store/store.vuex';
import { Invitation } from '@/entities/models/Invitation';
import { FloorPlan } from '@/entities/models/FloorPlan';
import { FloorPlanLocationData } from '@/entities/models/FloorplanLocationData';
import { DepartmentIssueSummary } from '@/entities/models/DepartmentIssue';
import { CustomerPermissions } from '../permissions/CustomerPermissions';
import { AdminPermissions } from '../permissions/AdminPermissions';
import AssetDevice, { Asset } from '@/entities/AssetDevice';
import LoggedUserManager from '../LoggedUserManager';
import { AcceptanceProtocol } from '@/entities/models/AcceptanceProtocol';
const userCan = LoggedUserManager.checkPermissions;
var vxDepartmentStore = vxm.departmentStore;
export default class DepartmentRepository {
    constructor() { }
    /**
     * Gets all departments for current user
     * @param [pagination]
     * @param [sort]
     * @param filters
     * @returns DepartmentArray
     */
    async getDepartments(pagination = null, sort = null, filters = []) {
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (filters.length > 0) {
            queries.addFilters(filters);
        }
        let url = ApiLinks.Department.Home;
        //If there are some queries defined, add them to url
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        let departmentsResponse = new ApiResponse();
        if (response?.status != ApiStatus.SUCCESS)
            return departmentsResponse;
        else {
            //Create response with pagination data
            departmentsResponse.setPagination(response.data);
            departmentsResponse.setData(Department.fromApi(response.data.data));
            return departmentsResponse;
        }
    }
    /**
     * Gets department by ID. If no ID is specified, currently selected department ID is used.
     * @param [departmentId]
     * @returns department
     */
    async getDepartment(departmentId = this.getSelectedDepartmentId()) {
        if (!departmentId)
            return null;
        let url = `${ApiLinks.Department.Home}/${departmentId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new Department();
        else
            return Department.fromApi(response.data.data);
    }
    async getDepartmentIssues() {
        let url = `${ApiLinks.Department.Home}${ApiLinks.Department.Keys.Issues}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status !== ApiStatus.SUCCESS)
            return [];
        else
            return DepartmentIssueSummary.fromApi(response.data.data);
    }
    /**
     * Updates department
     * @param data
     * @param [departmentId]
     * @returns department
     */
    async updateDepartment(data, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.COMPANY_MANAGE_OWN)) {
            throw 'Missing permissions for action updateDepartment';
        }
        let url = `${ApiLinks.Department.Home}/${departmentId}`;
        let response = await AxiosService.putDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return new Department();
        else
            return Department.fromApi(response.data.data);
    }
    /**
     * Gets department settings for given department ID
     * @param [departmentId]
     * @returns department settings
     */
    async getDepartmentSettings(departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Settings}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return DepartmentSettings.fromApi(response.data.data);
    }
    /**
     * Saves department setting
     * @param key Array<string>
     * @param value string | number | boolean
     * @param [departmentId]
     * @returns department setting object
     */
    async saveDepartmentSetting(key, value, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.COMPANY_MANAGE_OWN)) {
            throw 'Missing permissions for action saveDepartmentSetting';
        }
        let path = key.join('.');
        let userSetting = [{ Key: path, Value: value }];
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Settings}`;
        let response = await AxiosService.postDataToServer(url, userSetting);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return DepartmentSettings.fromApi(response.data.data);
    }
    async saveDepartmentPdfSetting(key, value, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.COMPANY_MANAGE_OWN)) {
            throw 'Missing permissions for action saveDepartmentPdfSetting';
        }
        let path = key.join('.');
        let userSetting = [{ Key: path, Value: value }];
        //let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Settings}/${path}`;
        //let response = await AxiosService.deleteDataFromServer(url, null );
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Settings}`;
        let response = await AxiosService.postDataToServer(url, userSetting);
        return response?.status == ApiStatus.SUCCESS;
    }
    /**
     * Invites users to department with given id
     * @param config list of email addresses + roles to send invitation link
     * @param [departmentId]
     * @returns boolean or response code
     */
    async inviteUsers(config, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.USERS_REGISTER)) {
            throw 'Missing permissions for action inviteUsers';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}${ApiLinks.Department.Keys.Invite}`;
        let response = await AxiosService.postDataToServer(url, config, false);
        if (response?.status === ApiStatus.NOT_ALLOWED)
            return response.data.code;
        else
            return response?.status == ApiStatus.SUCCESS;
    }
    /**
     * Gets all departments in system sorted by name (Admin call)
     * @returns List of AdminDepartment objects
     */
    async getAllDepartmentsAdmin(pagination = null, sort = null, filters = [], ignorePermissions = false) {
        let departmentAdminResponse = new ApiResponse();
        // Ignore permissions flag is used in router because there is one case,
        // where we don't have permissions loaded when this call is used.
        if (ignorePermissions == false && !userCan(AdminPermissions.ADMIN_DEPARTMENTS_READ)) {
            return departmentAdminResponse;
        }
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (filters.length > 0) {
            queries.addFilters(filters);
        }
        let url = ApiLinks.Department.AdminHome;
        // If there are some queries defined, add them to url
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return departmentAdminResponse;
        else {
            departmentAdminResponse.setPagination(response.data);
            departmentAdminResponse.setData(AdminDepartment.fromApi(response.data.data));
            return departmentAdminResponse;
        }
    }
    /**
     * Gets department by ID (Admin call)
     * @param [departmentId]
     * @returns AdminDepartment
     */
    async getDepartmentAdmin(departmentId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_READ)) {
            return new AdminDepartment();
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new AdminDepartment();
        else
            return AdminDepartment.fromApi(response.data.data);
    }
    /**
     * Gets admin department settings for given department ID
     * @param departmentId string
     * @returns department settings
     */
    async getAdminDepartmentSettings(departmentId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_SETTINGS_READ)) {
            throw 'Missing permissions for action getAdminDepartmentSettings';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Settings}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AdminDepartmentSettings.fromApi(response.data.data);
    }
    /**
     * Deletes admin department settings in given department
     * @param departmentId string
     * @param key string
     * @returns department settings
     */
    async deleteAdminDepartmentSettings(departmentId, key) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_SETTINGS_DELETE)) {
            throw 'Missing permissions for action deleteAdminDepartmentSettings';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Settings}/${key}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    /**
     * Delete department settings in given department
     * @param key array string
     * @param [departmentId]
     * @returns boolean or response code
     */
    async deleteDepartmentSettings(key, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.COMPANY_MANAGE_OWN)) {
            throw 'Missing permissions for action deleteDepartmentSettings';
        }
        let keyString = key.join('.');
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Settings}/${keyString}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.SUCCESS;
    }
    /**
     * Creates or update admin department settings for given department
     * @param departmentId string
     * @param dto IAdminDepartmentSettingsDTO
     * @returns department settings
     */
    async createOrUpdateAdminDepartmentSettings(departmentId, dto) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_SETTINGS_UPDATE)) {
            throw 'Missing permissions for action createOrUpdateAdminDepartmentSettings';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Settings}`;
        let response = await AxiosService.postDataToServer(url, [dto]);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AdminDepartmentSettings.fromApi(response.data.data);
    }
    /**
     * Gets all departments in system for given userId sorted by name (Admin call)
     * @returns List of AdminDepartment objects
     */
    async getAllDepartmentsForUserAdmin(userId) {
        if (!userCan(AdminPermissions.ADMIN_USERS_DEPARTMENTS_READ)) {
            return new Array();
        }
        let url = `${ApiLinks.Users.AdminHome}/${userId}${ApiLinks.Users.Keys.Departments}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return response.data.data;
    }
    /**
     * Create new empty Department
     */
    async adminCreateDepartment(data) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_CREATE)) {
            throw 'Missing permissions for action adminCreateDepartment';
        }
        let url = ApiLinks.Department.AdminHome;
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.CREATED)
            return null;
        else
            return AdminDepartment.fromApi(response.data.data);
    }
    /**
     * Update Department name
     */
    async adminUpdateDepartment(departmentId, data) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_UPDATE)) {
            throw 'Missing permissions for action adminUpdateDepartment';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}`;
        let response = await AxiosService.putDataToServer(url, data, false);
        if (response?.status != ApiStatus.SUCCESS)
            return response.data?.Code;
        else
            return AdminDepartment.fromApi(response.data.data);
    }
    /**
     * Delete Department
     */
    async deleteAdminDepartment(departmentId, returnAll) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_DELETE)) {
            return null;
        }
        let data = new FormData();
        data.set('ReturnComponents', returnAll.toString());
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}`;
        let response = await AxiosService.deleteDataFromServer(url, data);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    /**
     * Disable Department
     */
    async disableAdminDepartment(departmentId, reason) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_MANAGE)) {
            return null;
        }
        let data = { Reason: reason };
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Disable}`;
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AdminDepartment.fromApi(response.data.data);
    }
    /**
     * Enable Department
     */
    async enableAdminDepartment(departmentId) {
        if (!userCan(AdminPermissions.ADMIN_DEPARTMENTS_MANAGE)) {
            return null;
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.Enable}`;
        let response = await AxiosService.postDataToServer(url, null);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AdminDepartment.fromApi(response.data.data);
    }
    /**
     * Gets all department issues when logged from admin
     * @returns department issues summary for admin
     */
    async getAllDepartmentIssuesAdmin() {
        let url = `${ApiLinks.Department.AdminHome}${ApiLinks.Department.Keys.Issues}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status !== ApiStatus.SUCCESS)
            return [];
        else
            return DepartmentIssueSummary.fromApi(response.data.data);
    }
    async getAllDepartmentTagsAdmin() {
        let url = `${ApiLinks.Department.AdminHome}${ApiLinks.Department.Keys.Tags}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status !== ApiStatus.SUCCESS)
            return { Tags: [], Categories: [], Industries: [] };
        else
            return response.data.data;
    }
    async getSentInvitations(departmentId = this.getSelectedDepartmentId(), pagination = null, sort = null) {
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}${ApiLinks.Department.Keys.Invite}`;
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return Invitation.fromApi(response.data.data);
    }
    async cancelSentInvitation(invitationId, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.Users}${ApiLinks.Department.Keys.Invite}/${invitationId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async getFloorPlans(departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return FloorPlan.fromApi(response.data.data).sort((a, b) => (a.name > b.name ? 1 : -1));
    }
    async getFloorPlanData(floorPlanId, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}/${floorPlanId}${ApiLinks.Sources.Keys.Data}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return FloorPlanLocationData.fromApi(response.data.data);
    }
    async getFloorPlanImage(floorPlanId, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}/${floorPlanId}${ApiLinks.Sources.Keys.Image}`;
        let response = await AxiosService.downloadFile_GET(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response;
    }
    async getFloorPlanThumbnail(floorPlanId, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}/${floorPlanId}${ApiLinks.Sources.Keys.Thumbnail}`;
        let response = await AxiosService.downloadFile_GET(url, false);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response;
    }
    async uploadFloorPlan(name, image, positions, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}`;
        let data = new FormData();
        data.append('image', image);
        data.append('name', name);
        data.append('data', JSON.stringify(positions));
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status !== ApiStatus.CREATED)
            return response;
        else
            return FloorPlan.fromApi(response.data.data);
    }
    async updateFloorPlanData(floorPlanId, positions, departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}/${floorPlanId}${ApiLinks.Sources.Keys.Data}`;
        let data = new FormData();
        data.append('data', JSON.stringify(positions));
        let response = await AxiosService.putDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return [];
        else
            return FloorPlanLocationData.fromApi(response.data.data);
    }
    async updateFloorPlan(floorPlanId, name, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}/${floorPlanId}`;
        let data = new FormData();
        data.append('name', name);
        let response = await AxiosService.putDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return FloorPlan.fromApi(response.data.data);
    }
    async deleteFloorPlan(floorPlanId, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}/${floorPlanId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async deleteThermoMap(departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async uploadThermoMap(image, departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Sources.Keys.FloorPlan}`;
        let data = new FormData();
        data.append('image', image);
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status != ApiStatus.SUCCESS)
            return response;
        else
            return true;
    }
    async getContactInfo(departmentId = this.getSelectedDepartmentId()) {
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.ContactInfo}`;
        let response = await AxiosService.getDataFromServer(url, false);
        if (response?.status === ApiStatus.SUCCESS)
            return response?.data?.data;
        else
            throw new Error(`adminGetContactInfo failed: ${response?.status}`);
    }
    async getContactPhoto(departmentId = this.getSelectedDepartmentId()) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.Home}/${departmentId}${ApiLinks.Department.Keys.ContactInfo}${ApiLinks.Department.Keys.Photo}`;
        let response = await AxiosService.downloadFile_GET(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response;
    }
    async adminGetAllAssetDevicesForDepartment(departmentId, pagination, sort, filters = []) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_READ)) {
            throw 'Forbidden action adminGetAllAssetDevicesForDepartment';
        }
        let finalResponse = new ApiResponse();
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}`;
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (filters.length > 0) {
            queries.addFilters(filters);
        }
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS) {
            return finalResponse;
        }
        else {
            finalResponse.setPagination(response.data);
            finalResponse.setData(AssetDevice.fromApi(response.data.data));
            return finalResponse;
        }
    }
    async adminCreateAssetDevice(dto, departmentId) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_MANAGE)) {
            throw 'Forbidden action adminGetAllAssetDevicesForDepartment';
        }
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}`;
        let response = await AxiosService.postDataToServer(url, dto);
        if (response?.status != ApiStatus.CREATED)
            return null;
        else
            return AssetDevice.fromApi(response.data.data);
    }
    async adminUpdateAssetDevice(assetDeviceDTO, departmentId, assetDeviceId) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_MANAGE)) {
            throw 'Forbidden action adminUpdateAssetDevice';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}`;
        let response = await AxiosService.putDataToServer(url, assetDeviceDTO);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AssetDevice.fromApi(response.data.data);
    }
    async adminRemoveAssetDevice(departmentId, assetDeviceId) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_DELETE)) {
            throw 'Forbidden action adminReturnAssetDevice';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async adminReturnAssetDeviceFromDepartment(departmentId, assetDeviceId) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_MANAGE)) {
            throw 'Forbidden action adminReturnAssetDevice';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}${ApiLinks.Department.Keys.Return}`;
        let response = await AxiosService.postDataToServer(url, null);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AssetDevice.fromApi(response.data.data);
    }
    async getAllAssetDevicesForDepartment(pagination = null, sort = null, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ASSETS_DEVICES_READ)) {
            throw 'Forbidden action getAllAssetDevicesForDepartment';
        }
        let finalResponse = new ApiResponse();
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}`;
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS) {
            return finalResponse;
        }
        else {
            finalResponse.setPagination(response.data);
            finalResponse.setData(Asset.fromApi(response.data.data));
            return finalResponse;
        }
    }
    async getAssetDevice(assetDeviceId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ASSETS_DEVICES_READ)) {
            return new Asset();
        }
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new Asset();
        else
            return Asset.fromApi(response.data.data);
    }
    async getAssetDevicePhoto(assetDeviceId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ASSETS_DEVICES_READ)) {
            throw 'Forbidden action getAssetDevicePhoto';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}${ApiLinks.Assets.Keys.Photo}`;
        let response = await AxiosService.downloadFile_GET(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response;
    }
    async updateAssetDevicePhoto(photo, assetDeviceId, departmentId = this.getSelectedDepartmentId()) {
        if (!LoggedUserManager.isAdmin()) {
            throw 'Forbidden action updateAssetDevicePhoto';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}${ApiLinks.Assets.Keys.Photo}`;
        let data = new FormData();
        data.append('photo', photo);
        let response = await AxiosService.postDataToServer(url, data);
        if (response?.status !== ApiStatus.CREATED)
            return null;
        else
            return response.data.data;
    }
    async deleteAssetDevicePhoto(assetDeviceId, departmentId = this.getSelectedDepartmentId()) {
        if (!LoggedUserManager.isAdmin()) {
            throw 'Forbidden action deleteAssetDevicePhoto';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}${ApiLinks.Assets.Keys.Photo}`;
        let response = await AxiosService.deleteDataFromServer(url, null);
        return response?.status === ApiStatus.NO_CONTENT;
    }
    async adminGetAssetDevice(assetDeviceId, departmentId) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_READ)) {
            return new AssetDevice();
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return new AssetDevice();
        else
            return AssetDevice.fromApi(response.data.data);
    }
    async adminGetAssetDevicePhoto(assetDeviceId, departmentId) {
        if (!userCan(AdminPermissions.ADMIN_ASSETS_DEVICES_READ)) {
            throw 'Forbidden action adminGetAssetDevicePhoto';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AssetDevices}/${assetDeviceId}${ApiLinks.Assets.Keys.Photo}`;
        let response = await AxiosService.downloadFile_GET(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return response;
    }
    async getAllDepartmentAcceptanceProtocols(pagination, sort, filters = [], departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ACCEPTANCE_PROTOCOLS_MANAGE)) {
            throw 'Forbidden action getAllDepartmentAcceptanceProtocols';
        }
        let parsedResponse = new ApiResponse();
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AcceptanceProtocols}`;
        let queries = new ApiQueries();
        queries.addPagination(pagination);
        if (sort)
            queries.addSorts(sort);
        if (filters.length > 0)
            queries.addFilters(filters);
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS) {
            return parsedResponse;
        }
        else {
            parsedResponse.setPagination(response.data);
            parsedResponse.setData(AcceptanceProtocol.fromApi(response.data.data));
            return parsedResponse;
        }
    }
    async getDepartmentAcceptanceProtocol(protocolId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ACCEPTANCE_PROTOCOLS_MANAGE)) {
            throw 'Forbidden action getDepartmentAcceptanceProtocol';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AcceptanceProtocols}/${protocolId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AcceptanceProtocol.fromApi(response.data.data);
    }
    async acceptAcceptanceProtocol(protocolId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ACCEPTANCE_PROTOCOLS_MANAGE)) {
            throw 'Forbidden action acceptAcceptanceProtocol';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AcceptanceProtocols}/${protocolId}${ApiLinks.Department.Keys.Accept}`;
        let response = await AxiosService.postDataToServer(url, null);
        if (response?.status !== ApiStatus.SUCCESS)
            return null;
        else
            return AcceptanceProtocol.fromApi(response.data.data);
    }
    async returnAcceptanceProtocol(reasonDto, protocolId, departmentId = this.getSelectedDepartmentId()) {
        if (!userCan(CustomerPermissions.ACCEPTANCE_PROTOCOLS_MANAGE)) {
            throw 'Forbidden action returnAcceptanceProtocol';
        }
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.Home}/${departmentId}${ApiLinks.Department.Keys.AcceptanceProtocols}/${protocolId}${ApiLinks.Department.Keys.Reject}`;
        let response = await AxiosService.postDataToServer(url, reasonDto);
        if (response?.status !== ApiStatus.SUCCESS)
            return null;
        else
            return AcceptanceProtocol.fromApi(response.data.data);
    }
    async adminCreateAcceptanceProtocol(departmentId) {
        let url = `${ApiLinks.Department.AdminHome}/${departmentId}${ApiLinks.Department.Keys.AcceptanceProtocols}`;
        let response = await AxiosService.postDataToServer(url, null);
        if (response?.status != ApiStatus.CREATED)
            return null;
        else
            return AcceptanceProtocol.fromApi(response.data.data);
    }
    async adminGetDepartmentAcceptanceProtocol(protocolId) {
        let url = `${ApiLinks.AcceptanceProtocols.AdminHome}/${protocolId}`;
        let response = await AxiosService.getDataFromServer(url);
        if (response?.status != ApiStatus.SUCCESS)
            return null;
        else
            return AcceptanceProtocol.fromApi(response.data.data);
    }
    getSelectedDepartmentId() {
        return vxDepartmentStore.selectedDepartment.id;
    }
}
